import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Layers,
  FileBarChart,
  ListChecks,
  Settings as SettingsIcon,
  Users as UsersIcon,
  LogOut,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  X,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";

export const Sidebar = ({
  isMobile,
  isOpen,
  onClose,
  collapsed,
  onToggleCollapse,
}) => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const navItems = isAdmin
    ? [
        { to: "/", label: "Templates", icon: Layers, exact: true, testid: "nav-templates" },
        { to: "/batch-report", label: "Batch Report", icon: FileBarChart, testid: "nav-batch-report" },
        { to: "/batches", label: "Batches", icon: ListChecks, testid: "nav-batches" },
        { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
        { to: "/users", label: "Users", icon: UsersIcon, testid: "nav-users" },
      ]
    : [
        { to: "/batch-report", label: "Batch Report", icon: FileBarChart, testid: "nav-batch-report" },
        { to: "/batches", label: "Batches", icon: ListChecks, testid: "nav-batches" },
      ];

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const handleNavClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  const showCollapsed = !isMobile && collapsed;

  return (
    <>
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={[
          showCollapsed
            ? "w-[72px] bg-gradient-to-b from-[#76aff1] to-[#2E5C8A] border-r border-white/10 text-white shadow-sm"
            : "w-64 bg-gradient-to-b from-[#76aff1] to-[#2E5C8A] text-white",
          "flex flex-col h-screen transition-all duration-300",
          isMobile ? "fixed top-0 left-0 z-50 transition-transform duration-300" : "sticky top-0",
          isMobile && !isOpen ? "-translate-x-full" : "translate-x-0",
          "lg:translate-x-0",
        ].join(" ")}
      >
        <div
          className={`${
            showCollapsed
              ? "px-3 py-5 border-b border-white/10"
              : "p-2 border-b border-white/10"
          }`}
        >
          {showCollapsed ? (
            <div className="flex items-center justify-center">
              <button
                type="button"
                onClick={onToggleCollapse}
                className="w-10 h-10 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
                aria-label="Expand sidebar"
                data-testid="sidebar-collapse-btn"
              >
                <PanelLeftOpen className="w-4 h-4 text-white" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0 pr-2">
                <div className="w-full max-w-[220px] h-12 rounded-lg flex items-center justify-start overflow-hidden">
                  <img
                    src="/drlogo.png"
                    alt="Logo"
                    className="w-full h-12 object-contain object-left"
                  />
                </div>
              </div>

              {isMobile ? (
                <button
                  onClick={onClose}
                  className="lg:hidden p-1 hover:bg-white/10 rounded"
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onToggleCollapse}
                  className="shrink-0 p-2 hover:bg-white/10 rounded-lg transition-colors text-white"
                  aria-label="Collapse sidebar"
                  data-testid="sidebar-collapse-btn"
                >
                  <PanelLeftClose className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>

        <nav className={`${showCollapsed ? "px-2 py-4" : "px-2 py-3"} flex-1 overflow-y-auto`}>
          <div className={`${showCollapsed ? "space-y-3" : "space-y-0.5"}`}>
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.exact}
                  onClick={handleNavClick}
                  title={showCollapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    showCollapsed
                      ? `group flex items-center justify-center w-12 h-12 rounded-2xl border transition-all ${
                          isActive
                            ? "bg-white text-[#2E5C8A] border-white shadow-sm"
                            : "bg-white/10 border-white/10 hover:bg-white/20"
                        }`
                      : `group flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                          isActive
                            ? "bg-white text-[#2E5C8A] shadow-md"
                            : "text-white/90 hover:bg-white/10 hover:text-white"
                        }`
                  }
                  data-testid={item.testid}
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        className={
                          showCollapsed
                            ? `w-4 h-4 shrink-0 ${isActive ? "text-[#2E5C8A]" : "text-white/85 group-hover:text-white"}`
                            : `w-4 h-4 shrink-0 ${isActive ? "text-[#2E5C8A]" : "text-white/70 group-hover:text-white"}`
                        }
                        strokeWidth={2.5}
                      />
                      {!showCollapsed && (
                        <>
                          <div className="flex-1 min-w-0">
                            <div className={`font-semibold text-sm ${isActive ? "text-[#2E5C8A]" : ""}`}>
                              {item.label}
                            </div>
                          </div>
                          {isActive && (
                            <ChevronRight className="w-3.5 h-3.5 text-[#2E5C8A]" strokeWidth={3} />
                          )}
                        </>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        </nav>

        <div
          className={`${
            showCollapsed
              ? "p-2 border-t border-white/10"
              : "p-3 border-t border-white/10"
          }`}
        >
          {user && (
            <div>
              {!showCollapsed && (
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center font-heading font-black text-sm">
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-xs truncate">
                      {user.name || user.email}
                    </div>
                    <div className="text-[10px] text-white/60 uppercase tracking-wider">
                      {user.role}
                    </div>
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={handleLogout}
                title={showCollapsed ? "Sign Out" : undefined}
                className={
                  showCollapsed
                    ? "w-full h-12 rounded-2xl border border-white/10 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    : "w-full flex items-center justify-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-xs font-semibold"
                }
                data-testid="logout-btn"
              >
                <LogOut className="w-3.5 h-3.5 text-white" />
                {!showCollapsed && "Sign Out"}
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
